import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage, useT } from "@/lib/LanguageContext";
// Relative on purpose: real modules under Jest, where there is no "@" alias.
import FieldError from "../../components/ui/FieldError";
import { PATHWAYS_ADMIN as IDS } from "../../constants/testIds/pathwaysAdmin";
import { pathwayErrorMessage } from "../../pathways/errors";
import { attendanceChanges } from "../../pathways/usePathwayDashboard";
import { fmtSessionShort } from "./format";

/**
 * The Attendance section of ONE cohort live step (contract §10.0.3, §10.4, §10.11): every
 * learner with a checkbox, ticked when the server has them as attended, and Save, which
 * sends only the boxes that changed (`attendanceChanges`: the newly ticked as
 * `attended: true`, the newly unticked as `attended: false`).
 *
 * Ticking completes the step for that learner; unticking removes the mark but never
 * un-completes the step (contract §10.2), which the hint says.
 *
 * The parent keys this component by the stored attendance, so a re-read after Save starts
 * the boxes again from what the server now holds.
 *
 * @param {object} props
 * @param {{ step_id: string, title: string, starts_at: string|null, attended: string[] }} props.session
 * @param {{ email: string, name: string }[]} props.learners   the dashboard's learners
 * @param {(stepId: string, stored: string[], ticked: Set<string>) => Promise<number>} props.onSave
 * @param {boolean} props.disabled   an archived pathway takes no attendance
 */
export default function AttendancePanel({ session, learners, onSave, disabled }) {
  const t = useT("admin");
  const tc = useT("common");
  const { lang } = useLanguage();
  const [ticked, setTicked] = useState(() => new Set(session.attended));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const changed = attendanceChanges(session.attended, ticked).length > 0;
  // Presentation order only: a roll call reads alphabetically.
  const rows = useMemo(
    () => [...learners].sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email, lang)),
    [learners, lang],
  );
  const when = fmtSessionShort(session.starts_at, lang);
  const headingId = `pathway-attendance-heading-${session.step_id}`;

  const toggle = (email) => {
    setError("");
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email); else next.add(email);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await onSave(session.step_id, session.attended, ticked);
      toast.success(t("path.attendance.saved"));
    } catch (e) {
      setError(pathwayErrorMessage(e, tc, tc("errors.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid={IDS.attendance(session.step_id)} aria-labelledby={headingId} className="bg-white rounded-3xl border border-eh-border-soft p-5 space-y-3">
      <div>
        <h2 id={headingId} className="text-sm font-semibold text-eh-ink">
          {when ? t("path.attendance.title", { title: session.title, when }) : t("path.attendance.titleNoTime", { title: session.title })}
        </h2>
        <p className="text-sm text-eh-muted">{t("path.attendance.hint")}</p>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-eh-muted">{t("path.dash.noLearners")}</p>
      ) : (
        <ul className="grid gap-1 sm:grid-cols-2">
          {rows.map((l) => (
            <li key={l.email}>
              <label className="flex items-center gap-2 text-sm text-eh-ink">
                <input
                  type="checkbox"
                  checked={ticked.has(l.email)}
                  onChange={() => toggle(l.email)}
                  disabled={disabled || saving}
                  data-testid={IDS.attendanceBox(session.step_id, l.email)}
                  className="accent-eh-sage-deep"
                />
                {l.name || l.email}
              </label>
            </li>
          ))}
        </ul>
      )}
      <FieldError message={error} testId={IDS.attendanceError(session.step_id)} />
      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={disabled || saving || !changed}
          data-testid={IDS.attendanceSave(session.step_id)}
          className="bg-eh-ink text-eh-cream hover:bg-eh-ink/90"
        >
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />}
          {t("path.attendance.save")}
        </Button>
      </div>
    </section>
  );
}
